import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Tag,
  Select,
  Tooltip,
  Image,
} from "antd";
import { EyeOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import Modal from "../../components/Modal";
import { useGet, usePost, useDynamicApi } from "../../hooks/useApi";
import { useDebounce } from "../../hooks/useDebounce";
import { API_ENDPOINTS, API_BASE_URL } from "../../config/api";
import Toast from "../../utils/toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const TipManager = () => {
  const { t: translate, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTip, setSelectedTip] = useState(null);
  const [tipToDelete, setTipToDelete] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    category: "",
  });

  // Pagination and search
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [tipList, setTipList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    video_url: "",
    thumbnail_url: "",
  });

  const [formErrors, setFormErrors] = useState({});

  // File uploads
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  // API hooks
  const { execute: fetchList, loading: loadingList } = useGet(
    `${API_ENDPOINTS.TIP_SESSIONS_LIST}?page=${currentPage}&page_size=${pageSize}&search=${debouncedSearchTerm}&category=${filters.category || ""}`,
  );

  const { post, loading: creating } = usePost(API_ENDPOINTS.TIP_SESSIONS);
  const { execute: getById, loading: loadingById } = useDynamicApi("GET");
  const { execute: putData, loading: updating } = useDynamicApi("PUT");
  const { execute: deleteData, loading: deleting } = useDynamicApi("DELETE");

  const loading =
    loadingList ||
    loadingById ||
    updating ||
    deleting ||
    creating ||
    uploadingVideo ||
    uploadingThumbnail ||
    submitting;

  // Load tip list
  const loadData = async () => {
    try {
      const response = await fetchList();
      if (response) {
        setTipList(response.data || []);
        setTotalCount(response.pagination?.total_count || 0);
      }
    } catch (error) {
      console.error("Error loading tips:", error);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    loadData();
  }, [debouncedSearchTerm, filters.category]);

  // File upload function (exactly like ExerciseContentManager)
  const uploadFile = async (file, type) => {
    if (!file) return null;
    const setUploading =
      type === "video" ? setUploadingVideo : setUploadingThumbnail;
    setUploading(true);
    try {
      const token =
        localStorage.getItem("adminToken") ||
        localStorage.getItem("access_token") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("adminToken") ||
        sessionStorage.getItem("access_token");

      if (!token) {
        Toast.error(translate("common.error"));
        setUploading(false);
        return null;
      }

      const formDataObj = new FormData();
      formDataObj.append("image", file);

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.UPLOAD_MEDIA}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formDataObj,
        },
      );

      const result = await response.json();

      if (response.ok && result.image_url) {
        Toast.success(translate("tipManager.uploadSuccess"));
        return result.image_url;
      } else {
        const errorMessage =
          result.message ||
          result.detail ||
          translate("tipManager.uploadFailed");
        Toast.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      Toast.error(error.message || translate("tipManager.uploadFailed"));
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleVideoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      "video/mp4",
      "video/mov",
      "video/avi",
      "video/mkv",
      "video/webm",
    ];
    if (!validTypes.includes(file.type)) {
      Toast.error(translate("tipManager.invalidVideoType"));
      e.target.value = "";
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      Toast.error(translate("tipManager.fileSizeExceeded"));
      e.target.value = "";
      return;
    }

    setVideoFile(file);
    Toast.info(translate("tipManager.uploadingVideo"));

    const videoUrl = await uploadFile(file, "video");
    if (videoUrl) {
      setFormData((prev) => ({ ...prev, video_url: videoUrl }));
      Toast.success(translate("tipManager.videoUploaded"));
    } else {
      setVideoFile(null);
      e.target.value = "";
    }
  };

  const handleThumbnailSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      Toast.error(translate("tipManager.invalidImageType"));
      e.target.value = "";
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      Toast.error(translate("tipManager.imageSizeExceeded"));
      e.target.value = "";
      return;
    }

    setThumbnailFile(file);
    Toast.info(translate("tipManager.uploadingThumbnail"));

    const thumbnailUrl = await uploadFile(file, "thumbnail");
    if (thumbnailUrl) {
      setFormData((prev) => ({ ...prev, thumbnail_url: thumbnailUrl }));
      Toast.success(translate("tipManager.thumbnailUploaded"));
    } else {
      setThumbnailFile(null);
      e.target.value = "";
    }
  };

  // Form handling
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim())
      errors.title = translate("tipManager.titleRequired");
    else if (formData.title.trim().length < 2)
      errors.title = translate("tipManager.titleMinLength");
    else if (formData.title.trim().length > 200)
      errors.title = translate("tipManager.titleMaxLength");

    if (!formData.category)
      errors.category = translate("tipManager.categoryRequired");

    if (!formData.description?.trim())
      errors.description = translate("tipManager.descriptionRequired");
    else if (formData.description.trim().length < 10)
      errors.description = translate("tipManager.descriptionMinLength");

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      category: "",
      video_url: "",
      thumbnail_url: "",
    });
    setFormErrors({});
    setVideoFile(null);
    setThumbnailFile(null);
    setSelectedTip(null);
    setIsEditMode(false);
  };

  const resetFilters = () => {
    setFilters({ search: "", category: "" });
    setSearchTerm("");
  };

  // Create tip
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const response = await post(formData);
      if (response?.status === true) {
        Toast.success(
          response?.message || translate("tipManager.createdSuccess"),
        );
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) setFormErrors(response.errors);
        else
          Toast.error(
            response?.message || translate("tipManager.failedCreate"),
          );
      }
    } catch (error) {
      console.error(error);
      Toast.error(translate("tipManager.failedCreate"));
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch tip by id
  const fetchTipById = async (id) => {
    const endpoint = `${API_ENDPOINTS.TIP_SESSIONS_DETAIL_BY_ID}/${id}`;
    return await getById(endpoint);
  };

  // Update tip
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedTip?.id) return;
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const endpoint = `${API_ENDPOINTS.TIP_SESSIONS_DETAIL_BY_ID}/${selectedTip.id}`;
      const response = await putData(endpoint, formData);
      if (response?.status === true) {
        Toast.success(
          response?.message || translate("tipManager.updatedSuccess"),
        );
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) setFormErrors(response.errors);
        else
          Toast.error(
            response?.message || translate("tipManager.failedUpdate"),
          );
      }
    } catch (error) {
      console.error(error);
      Toast.error(translate("tipManager.failedUpdate"));
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = async (tip) => {
    try {
      const response = await fetchTipById(tip.id);
      if (response?.status === true && response.data) {
        setSelectedTip(response.data);
        setFormData({
          title: response.data.title || "",
          description: response.data.description || "",
          category: response.data.category || "",
          video_url: response.data.video_url || "",
          thumbnail_url: response.data.thumbnail_url || "",
        });
        setIsEditMode(true);
        setIsModalOpen(true);
      } else {
        Toast.error(translate("tipManager.failedFetchDetails"));
      }
    } catch (error) {
      console.error(error);
      Toast.error(translate("tipManager.failedFetchDetails"));
    }
  };

  const openDeleteModal = (tip) => {
    setTipToDelete(tip);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tipToDelete) return;
    try {
      const endpoint = `${API_ENDPOINTS.TIP_SESSIONS_DETAIL_BY_ID}/${tipToDelete.id}`;
      const response = await deleteData(endpoint);
      if (response?.status === true) {
        Toast.success(
          response?.message || translate("tipManager.deletedSuccess"),
        );
        await loadData();
      } else {
        Toast.error(response?.message || translate("tipManager.failedDelete"));
      }
    } catch (error) {
      Toast.error(translate("tipManager.failedDelete"));
    } finally {
      setIsDeleteModalOpen(false);
      setTipToDelete(null);
    }
  };

  const openViewPage = (id) => {
    navigate(`/tips/details/${id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const CATEGORY_CHOICES = [
    { value: "COACHING", label: translate("tipCategories.COACHING") },
    { value: "TECHNIQUE", label: translate("tipCategories.TECHNIQUE") },
    { value: "GAME", label: translate("tipCategories.GAME") },
    { value: "YOUTH", label: translate("tipCategories.YOUTH") },
  ];

  const columns = [
    {
      title: "Sr. No.",
      key: "serial",
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
      width: 70,
      align: "center",
    },
    {
      title: translate("tipManager.title"),
      dataIndex: "title",
      key: "title",
    },
    {
      title: translate("tipManager.category"),
      dataIndex: "category",
      key: "category",
      render: (cat) => {
        const choice = CATEGORY_CHOICES.find((c) => c.value === cat);
        return choice ? choice.label : cat;
      },
    },
    {
      title: translate("tipManager.description"),
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (text) =>
        text?.length > 60 ? text.substring(0, 60) + "..." : text || "N/A",
    },
    {
      title: translate("tipManager.thumbnail"),
      dataIndex: "thumbnail_url",
      key: "thumbnail_url",
      render: (url) =>
        url ? (
          <Image
            src={url}
            width={50}
            height={50}
            style={{ objectFit: "cover" }}
          />
        ) : (
          "N/A"
        ),
    },
    {
      title: translate("common.createdAt"),
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => formatDate(date),
    },
    {
      title: translate("common.actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title={translate("common.view")}>
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openViewPage(record.id)}
            />
          </Tooltip>
          <Tooltip title={translate("common.edit")}>
            <Button
              style={{ background: "#52c41a", borderColor: "#52c41a" }}
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title={translate("common.delete")}>
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => openDeleteModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const hasFilters = searchTerm || filters.category;

  return (
    <>
      <Card
        title={translate("tipManager.titleMain")}
        extra={
          <Button
            type="primary"
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
            disabled={loading}
          >
            {translate("tipManager.createNew")}
          </Button>
        }
      >
        {/* Filters */}
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            placeholder={translate("tipManager.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            style={{ width: 280 }}
            disabled={loading}
          />
          <Select
            placeholder={translate("tipManager.category")}
            value={filters.category || undefined}
            onChange={(v) => setFilters((p) => ({ ...p, category: v || "" }))}
            style={{ width: 180 }}
            allowClear
            options={CATEGORY_CHOICES}
            disabled={loading}
          />
          {hasFilters && (
            <Button onClick={resetFilters} disabled={loading}>
              {translate("common.clearFilters")}
            </Button>
          )}
        </Space>

        <div className="table-responsive">
          <Table
            dataSource={tipList}
            columns={columns}
            rowKey="id"
            loading={loading}
            locale={{
              emptyText: hasFilters
                ? translate("common.noMatchingEntries")
                : translate("tipManager.noTipsFound"),
            }}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalCount,
              showSizeChanger: true,
              pageSizeOptions: ["5", "10", "20", "50", "100"],
              showTotal: (total) =>
                `${translate("common.showing")} ${(currentPage - 1) * pageSize + 1} ${translate("common.to")} ${Math.min(
                  currentPage * pageSize,
                  total,
                )} ${translate("common.of")} ${total} ${translate("tipManager.tips")}`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 10);
              },
            }}
          />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
          }}
          title={
            isEditMode
              ? translate("tipManager.editTitle")
              : translate("tipManager.createTitle")
          }
          onConfirm={isEditMode ? handleUpdate : handleCreate}
          confirmText={
            isEditMode
              ? submitting
                ? translate("tipManager.updating")
                : translate("common.update")
              : submitting
                ? translate("tipManager.creating")
                : translate("common.create")
          }
          confirmDisabled={submitting || uploadingVideo || uploadingThumbnail}
          size="large"
        >
          <form onSubmit={isEditMode ? handleUpdate : handleCreate}>
            {/* Title */}
            <div className="form-group">
              <label htmlFor="title">
                {translate("tipManager.title")}{" "}
                <span className="required">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder={translate("tipManager.enterTitle")}
                disabled={submitting}
                className={formErrors.title ? "input-error" : ""}
                maxLength={200}
              />
              <div
                style={{ fontSize: "12px", color: "#666", textAlign: "right" }}
              >
                {formData.title.length}/200 {translate("common.characters")}
              </div>
              {formErrors.title && (
                <div className="error-message">{formErrors.title}</div>
              )}
            </div>

            {/* Category */}
            <div className="form-group">
              <label htmlFor="category">
                {translate("tipManager.category")}{" "}
                <span className="required">*</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                disabled={submitting}
                className={formErrors.category ? "input-error" : ""}
              >
                <option value="">
                  {translate("tipManager.selectCategory")}
                </option>
                {CATEGORY_CHOICES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {formErrors.category && (
                <div className="error-message">{formErrors.category}</div>
              )}
            </div>

            {/* Description */}
            <div className="form-group">
              <label htmlFor="description">
                {translate("tipManager.description")}{" "}
                <span className="required">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                rows="4"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={translate("tipManager.enterDescription")}
                disabled={submitting}
                className={formErrors.description ? "input-error" : ""}
              />
              {formErrors.description && (
                <div className="error-message">{formErrors.description}</div>
              )}
            </div>

            {/* Video Upload */}
            <div className="form-group">
              <label htmlFor="video_file">
                {translate("tipManager.uploadVideo")}
              </label>
              <div className="file-upload">
                <input
                  type="file"
                  id="video_file"
                  accept="video/*"
                  onChange={handleVideoSelect}
                  disabled={submitting || uploadingVideo}
                />
                {videoFile && !uploadingVideo && (
                  <div className="file-info">
                    <span className="file-name">{videoFile.name}</span>
                    <span className="file-size">
                      {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                  </div>
                )}
                {uploadingVideo && (
                  <div className="upload-progress">
                    <div className="progress-text">
                      {translate("tipManager.uploadingVideo")}
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill"></div>
                    </div>
                  </div>
                )}
                {formData.video_url && !uploadingVideo && (
                  <div className="upload-success">
                    <span className="success-icon">✓</span>
                    <span>{translate("tipManager.videoUploaded")}</span>
                    <video
                      src={formData.video_url}
                      width="200"
                      controls
                      style={{ marginTop: 8, display: "block" }}
                    />
                  </div>
                )}
              </div>
              <div className="form-help">
                {translate("tipManager.videoHelp")}
              </div>
            </div>

            {/* Thumbnail Upload */}
            <div className="form-group">
              <label htmlFor="thumbnail_file">
                {translate("tipManager.uploadThumbnail")}
              </label>
              <div className="file-upload">
                <input
                  type="file"
                  id="thumbnail_file"
                  accept="image/*"
                  onChange={handleThumbnailSelect}
                  disabled={submitting || uploadingThumbnail}
                />
                {thumbnailFile && !uploadingThumbnail && (
                  <div className="file-info">
                    <span className="file-name">{thumbnailFile.name}</span>
                    <span className="file-size">
                      {(thumbnailFile.size / 1024).toFixed(2)} KB
                    </span>
                  </div>
                )}
                {uploadingThumbnail && (
                  <div className="upload-progress">
                    <div className="progress-text">
                      {translate("tipManager.uploadingThumbnail")}
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill"></div>
                    </div>
                  </div>
                )}
                {formData.thumbnail_url && !uploadingThumbnail && (
                  <div className="upload-success">
                    <span className="success-icon">✓</span>
                    <span>{translate("tipManager.thumbnailUploaded")}</span>
                    <Image
                      src={formData.thumbnail_url}
                      width={100}
                      style={{ marginTop: 8, borderRadius: 4 }}
                    />
                  </div>
                )}
              </div>
              <div className="form-help">
                {translate("tipManager.thumbnailHelp")}
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && tipToDelete && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setTipToDelete(null);
          }}
          title={translate("tipManager.confirmDelete")}
          onConfirm={handleDeleteConfirm}
          confirmText={
            deleting ? translate("common.deleting") : translate("common.delete")
          }
          confirmColor="danger"
          confirmDisabled={deleting}
        >
          <div className="delete-confirmation">
            <div className="warning-icon">⚠️</div>
            <h3>{translate("tipManager.confirmDeleteMessage")}</h3>
            <div className="item-details">
              <p>
                <strong>{translate("tipManager.title")}:</strong>{" "}
                {tipToDelete.title}
              </p>
              <p>
                <strong>{translate("tipManager.category")}:</strong>{" "}
                {CATEGORY_CHOICES.find((c) => c.value === tipToDelete.category)
                  ?.label || tipToDelete.category}
              </p>
              <p>
                <strong>{translate("common.createdAt")}:</strong>{" "}
                {formatDate(tipToDelete.created_at)}
              </p>
            </div>
            <p className="warning-text">
              {translate("tipManager.deleteWarning")}
            </p>
          </div>
        </Modal>
      )}
    </>
  );
};

export default TipManager;
